﻿import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AlertTriangle, Loader2, Lock, Unlock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { useCompany } from "@/lib/CompanyProvider";
import { supabase } from "@/integrations/supabase/client";
import { useFinancialData } from "@/hooks/financial/useFinancialData";
import { FinancialMetrics } from "@/components/financial/FinancialMetrics";
import { TransactionLists } from "@/components/financial/TransactionLists";
import {
  OpenRegisterModal,
  CloseRegisterModal,
  ExpenseModal,
} from "@/components/financial/FinancialModals";

export const Route = createFileRoute("/app/financial")({
  component: FinancialDashboard,
});

function FinancialDashboard() {
  const { activeCompanyId } = useCompany();
  const today = new Date();

  const [isOpening, setIsOpening] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);

  const {
    openRegister,
    transactions,
    loading,
    openCashRegister,
    closeCashRegister,
    registerExpense,
  } = useFinancialData(activeCompanyId);

  // Pending commissions
  const { data: pendingCommissions = 0 } = useQuery({
    queryKey: ["pending_commissions", activeCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("collaborator_commissions")
        .select("amount")
        .eq("company_id", activeCompanyId)
        .eq("status", "PENDING");
      if (error) throw error;
      return data.reduce((sum, c) => sum + Number(c.amount), 0);
    },
    enabled: !!activeCompanyId && isClosing,
  });

  if (loading) {
    return (
      <div className="p-8 text-white flex justify-center">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  const entradas = transactions.filter((t: any) => t.type === "ENTRADA");
  const saidas = transactions.filter((t: any) => t.type === "SAIDA");

  const grossRevenue = entradas.reduce(
    (sum: number, t: any) => sum + (t.amount || 0),
    0,
  );
  const totalExpenses = saidas.reduce(
    (sum: number, t: any) => sum + (t.amount || 0),
    0,
  );

  const expectedPhysical =
    (openRegister?.initial_balance || 0) +
    entradas
      .filter((t: any) => t.method === "DINHEIRO")
      .reduce((sum: number, t: any) => sum + Number(t.amount), 0) -
    saidas
      .filter((t: any) => t.method === "DINHEIRO")
      .reduce((sum: number, t: any) => sum + Number(t.amount), 0);

  const expectedDigital =
    entradas
      .filter((t: any) => t.method !== "DINHEIRO")
      .reduce((sum: number, t: any) => sum + Number(t.amount), 0) -
    saidas
      .filter((t: any) => t.method !== "DINHEIRO")
      .reduce((sum: number, t: any) => sum + Number(t.amount), 0);

  const expectedTotal = expectedPhysical + expectedDigital;

  const currentBalance = grossRevenue - totalExpenses;
  const netMargin =
    grossRevenue > 0 ? ((currentBalance / grossRevenue) * 100).toFixed(1) : 0;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            Caixa e Finanças
          </h1>
          <p className="text-slate-400 mt-1">
            Resumo financeiro{" "}
            {openRegister
              ? "da Sessão Atual"
              : `do dia ${format(today, "dd 'de' MMMM", { locale: ptBR })}`}
          </p>
        </div>
        <div className="flex gap-3">
          {openRegister ? (
            <Button
              onClick={() => setIsClosing(true)}
              className="bg-red-600 hover:bg-red-500 text-white font-medium"
            >
              <Lock size={18} className="mr-2" />
              Fechar Caixa
            </Button>
          ) : (
            <Button
              onClick={() => setIsOpening(true)}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-medium"
            >
              <Unlock size={18} className="mr-2" />
              Abrir Caixa
            </Button>
          )}
        </div>
      </div>

      {!openRegister && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-4">
          <AlertTriangle className="text-amber-500 shrink-0 mt-1" />
          <div>
            <h3 className="text-amber-500 font-bold">O Caixa está Fechado</h3>
            <p className="text-amber-400/80 text-sm mt-1">
              Nenhuma transação pode ser recebida ou despesa lançada até que um
              novo caixa seja aberto. Abra o caixa para iniciar o turno.
            </p>
          </div>
        </div>
      )}

      <FinancialMetrics
        currentBalance={currentBalance}
        initialBalance={openRegister?.initial_balance || 0}
        grossRevenue={grossRevenue}
        totalExpenses={totalExpenses}
        netMargin={netMargin}
      />

      <TransactionLists
        entradas={entradas}
        saidas={saidas}
        openRegister={openRegister}
        setIsExpenseModalOpen={setIsExpenseModalOpen}
      />

      <OpenRegisterModal
        isOpen={isOpening}
        setIsOpen={setIsOpening}
        openCashRegister={openCashRegister}
      />

      <CloseRegisterModal
        isOpen={isClosing}
        setIsOpen={setIsClosing}
        pendingCommissions={pendingCommissions}
        expectedTotal={expectedTotal}
        closeCashRegister={closeCashRegister}
      />

      <ExpenseModal
        isOpen={isExpenseModalOpen}
        setIsOpen={setIsExpenseModalOpen}
        registerExpense={registerExpense}
      />
    </div>
  );
}
